/**
 * Lighten Friends / Fun brand colors just enough for dark glass UIs.
 * Keep them close to brand — only a subtle lift so they stay perceptible.
 * Other F’s keep their exact brand hex (identity unchanged).
 */

function relativeLuminance(r, g, b) {
    const toLin = (c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
}

function parseHex(hex) {
    if (!hex || typeof hex !== 'string') return null;
    const raw = hex.replace('#', '').trim();
    const full =
        raw.length === 3
            ? raw
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : raw;
    if (full.length !== 6 || !/^[0-9a-fA-F]+$/.test(full)) return null;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) return null;
    return { r, g, b };
}

function normalizeHex(hex) {
    if (!hex || typeof hex !== 'string') return '';
    const raw = hex.replace('#', '').trim();
    const full =
        raw.length === 3
            ? raw
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : raw;
    return full.toUpperCase();
}

/** Brand hexes that read as near-black on dark glass without a slight lift. */
const DARK_BRAND_HEX = new Set([
    '022B7A', // Fun
    '700460', // Friends
]);

/**
 * Subtle lighten toward white — stays close to brand, just readable on dark UI.
 * Default mix is gentle (~12–22%).
 */
function lightenTowardWhite(hex, { minL = 0.14, maxMix = 0.22 } = {}) {
    const rgb = parseHex(hex);
    if (!rgb) return hex || '#c9a227';
    const { r, g, b } = rgb;
    const L = relativeLuminance(r, g, b);
    const normalized = hex.startsWith('#') ? hex : `#${hex.replace('#', '')}`;
    if (L >= minL) {
        return normalized;
    }
    const t = Math.min(maxMix, ((minL - L) / minL) * 0.16 + 0.1);
    const nr = Math.round(r + (255 - r) * t);
    const ng = Math.round(g + (255 - g) * t);
    const nb = Math.round(b + (255 - b) * t);
    return `rgb(${nr}, ${ng}, ${nb})`;
}

/** True for Friends / Fun areas (by name or brand hex). */
export function isDarkFArea(name = '', hex = '') {
    const n = String(name).trim().toLowerCase();
    if (n === 'fun' || n.includes('friend')) return true;
    return DARK_BRAND_HEX.has(normalizeHex(hex));
}

/**
 * Portal V2 display color: slight lift for Friends/Fun only; other F’s unchanged.
 */
export function readableFColor(name, hex) {
    if (!isDarkFArea(name, hex)) return hex;
    return lightenTowardWhite(hex, { minL: 0.16, maxMix: 0.24 });
}

/** @deprecated Prefer readableFColor(name, hex) — name-gated for Friends/Fun. */
export function readableBrandColor(hex, opts) {
    return lightenTowardWhite(hex, opts);
}
